import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@/store'
import { fetchClientTypesAction } from '@/store/clients/clients.actions'
import {
  fetchCountriesAction,
  fetchTickmillCompaniesAction,
} from '@/store/identity/identity.actions'
import {
  getWalletCurrenciesAction,
  getWalletPaymentProvidersActions,
  getWalletTypesAction,
} from '@/store/wallet/wallet-actions'
import { useApiClient } from '@/utils/api/ApiClient'
import { ClientsApiClient } from '@/utils/api/clientsApi'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { WalletsApiClient } from '@/utils/api/walletsApiClient'
import { isEmpty } from '@/utils/is-empty'

export const useWithdrawalAutomationOptions = () => {
  const dispatch = useAppDispatch()
  const walletsApiClient = useApiClient(WalletsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)

  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)
  const countries = useAppSelector((state) => state.identity.countries)
  const paymentProviders = useAppSelector((state) => state.wallet.paymentProviders)
  const clientTypes = useAppSelector((state) => state.clients.types)
  const walletTypes = useAppSelector((state) => state.wallet.walletType)
  const currencies = useAppSelector((state) => state.wallet.currencies)

  useEffect(() => {
    const fetchData = async () => {
      if (isEmpty(tickmillCompanies)) {
        await dispatch(fetchTickmillCompaniesAction(identityApiClient))
      }
      if (isEmpty(countries)) {
        await dispatch(fetchCountriesAction(identityApiClient))
      }
      if (isEmpty(paymentProviders)) {
        await dispatch(getWalletPaymentProvidersActions(walletsApiClient))
      }
      if (isEmpty(clientTypes)) {
        await dispatch(fetchClientTypesAction(clientsApiClient))
      }
      if (isEmpty(walletTypes)) {
        await dispatch(getWalletTypesAction(walletsApiClient))
      }
      if (isEmpty(currencies)) {
        await dispatch(getWalletCurrenciesAction(walletsApiClient))
      }
    }

    fetchData().catch(console.error)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const companyOptions = useMemo(
    () => tickmillCompanies.map((company) => ({ id: company.id, name: company.name })),
    [tickmillCompanies]
  )

  const countryOptions = useMemo(
    () => countries.map((country) => ({ id: country.id, name: country.name })),
    [countries]
  )

  const paymentProviderOptions = useMemo(
    () => paymentProviders.map((provider) => ({ id: provider.id, name: provider.name })),
    [paymentProviders]
  )

  const clientTypeOptions = useMemo(
    () => clientTypes.map((type) => ({ id: type.id, name: type.name })),
    [clientTypes]
  )

  const walletTypeOptions = useMemo(
    () => walletTypes.map((type) => ({ id: type.id, name: type.name })),
    [walletTypes]
  )

  const currencyOptions = useMemo(
    () => currencies.map((currency) => ({ id: currency.id, name: currency.id })),
    [currencies]
  )

  return {
    companyOptions,
    countryOptions,
    paymentProviderOptions,
    clientTypeOptions,
    walletTypeOptions,
    currencyOptions,
  }
}
